import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { authAPI } from '@/utils/api';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';

// Eager load only critical pages
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import Dashboard from '@/pages/Dashboard';
// Admin pages
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminUserDetail from '@/pages/admin/AdminUserDetail';
import AdminCommunications from '@/pages/admin/AdminCommunications';
import AdminSecurity from '@/pages/admin/AdminSecurity';
import AdminCases from '@/pages/admin/AdminCases';
import AdminFinance from '@/pages/admin/AdminFinance';
import AdminIntegrations from '@/pages/admin/AdminIntegrations';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminAudit from '@/pages/admin/AdminAudit';
import AdminAdmins from '@/pages/admin/AdminAdmins';

// Lazy load all other pages for better performance
const Analytics = lazy(() => import('@/pages/Analytics'));
const NotificationSettings = lazy(() => import('@/pages/NotificationSettings'));
const Help = lazy(() => import('@/pages/Help'));
const ActivityLog = lazy(() => import('@/pages/ActivityLog'));
const ClinicalScore = lazy(() => import('@/pages/ClinicalScore'));
const Calendar = lazy(() => import('@/pages/Calendar'));
const InfoBank = lazy(() => import('@/pages/InfoBank'));
const Moments = lazy(() => import('@/pages/Moments'));
const Plans = lazy(() => import('@/pages/Plans'));
const MyCase = lazy(() => import('@/pages/MyCase'));
const CalendarTools = lazy(() => import('@/pages/CalendarTools'));
const Messages = lazy(() => import('@/pages/Messages'));
const Expenses = lazy(() => import('@/pages/Expenses'));
const Documents = lazy(() => import('@/pages/Documents'));
const Reports = lazy(() => import('@/pages/Reports'));
const ScheduleChanges = lazy(() => import('@/pages/ScheduleChanges'));
const Children = lazy(() => import('@/pages/Children'));
const ParentingPlans = lazy(() => import('@/pages/ParentingPlans'));
const Professionals = lazy(() => import('@/pages/Professionals'));
const Settings = lazy(() => import('@/pages/Settings'));
const Billing = lazy(() => import('@/pages/Billing'));
const Invoices = lazy(() => import('@/pages/Invoices'));
const Calls = lazy(() => import('@/pages/Calls'));
const Waiver = lazy(() => import('@/pages/Waiver'));
const ContactUs = lazy(() => import('@/pages/ContactUs'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const TextMessages = lazy(() => import('@/pages/TextMessages'));
const VideoCall = lazy(() => import('@/pages/VideoCall'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const CaseFiles = lazy(() => import('@/pages/CaseFiles'));
const CaseFileDetail = lazy(() => import('@/pages/CaseFileDetail'));
const AttorneyDashboard = lazy(() => import('@/pages/AttorneyDashboard'));
const InviteParent = lazy(() => import('@/pages/InviteParent'));
const TwilioIntegration = lazy(() => import('@/pages/TwilioIntegration'));
const StripeIntegration = lazy(() => import('@/pages/StripeIntegration'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-slate-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500 mx-auto mb-4"></div>
      <p className="text-slate-300 text-lg">Loading...</p>
    </div>
  </div>
);

// Dashboard Router Component
const DashboardRouter = ({ setIsAuthenticated }) => {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        // Use runtime config for backend URL, empty string = same-origin
        const backendUrl = window.RUNTIME_CONFIG?.BACKEND_URL ?? process.env.REACT_APP_BACKEND_URL ?? '';
        const apiUrl = backendUrl ? `${backendUrl}/api/auth/me` : '/api/auth/me';
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const data = await response.json();
        setUser(data);
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }

    // Route admin to admin dashboard
    if (user?.role === 'admin') {
          return <Navigate to="/admin" replace />;
    }
  // Route attorneys, court_reps, and providers to attorney dashboard
  if (['attorney', 'court_rep', 'provider'].includes(user?.role)) {
    return <AttorneyDashboard setIsAuthenticated={setIsAuthenticated} />;
  }

  // Route parents to regular dashboard
  return <Dashboard setIsAuthenticated={setIsAuthenticated} />;
};

// Internal component that uses AuthContext
function AppContent() {
  const { isAuthenticated, loading, setIsAuthenticated, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route
            path="/auth"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AuthPage setIsAuthenticated={setIsAuthenticated} />
              )
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                <DashboardRouter setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/analytics"
            element={
              isAuthenticated ? (
                <Analytics setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/clinical-score"
            element={
              isAuthenticated ? (
                <ClinicalScore setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/help"
            element={
              isAuthenticated ? (
                <Help setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/activity-log"
            element={
              isAuthenticated ? (
                <ActivityLog setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/calendar"
            element={
              isAuthenticated ? (
                <Calendar setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/moments"
            element={
              isAuthenticated ? (
                <Moments setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/my-case"
            element={
              isAuthenticated ? (
                <MyCase setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/plans"
            element={
              isAuthenticated ? (
                <Plans setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/info-bank"
            element={
              isAuthenticated ? (
                <InfoBank setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/calendar-tools"
            element={
              isAuthenticated ? (
                <CalendarTools setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/messages"
            element={
              isAuthenticated ? (
                <Messages setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/expenses"
            element={
              isAuthenticated ? (
                <Expenses setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/documents"
            element={
              isAuthenticated ? (
                <Documents setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/reports"
            element={
              isAuthenticated ? (
                <Reports setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/schedule-changes"
            element={
              isAuthenticated ? (
                <ScheduleChanges setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/children"
            element={
              isAuthenticated ? (
                <Children setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/parenting-plans"
            element={
              isAuthenticated ? (
                <ParentingPlans setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/professionals"
            element={
              isAuthenticated ? (
                <Professionals setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <Settings setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/notifications"
            element={
              isAuthenticated ? (
                <NotificationSettings setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/billing"
            element={
              isAuthenticated ? (
                <Billing setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/invoices"
            element={
              isAuthenticated ? (
                <Invoices setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/calls"
            element={
              isAuthenticated ? (
                <Calls setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/video-call"
            element={
              isAuthenticated ? (
                <VideoCall setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LandingPage />
              )
            }
          />
          <Route
            path="/waiver"
            element={
              isAuthenticated ? (
                <Waiver setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/contact"
            element={
              isAuthenticated ? (
                <ContactUs setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/verify-email"
            element={<VerifyEmail />}
          />
          <Route
            path="/text-messages"
            element={
              isAuthenticated ? (
                <TextMessages setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/case-files"
            element={
              isAuthenticated && ['attorney', 'court_rep', 'provider', 'admin'].includes(user?.role) ? (
                <CaseFiles setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to={isAuthenticated ? "/dashboard" : "/auth"} replace />
              )
            }
          />
          <Route
            path="/invite-parent"
            element={
              isAuthenticated && ['attorney', 'court_rep', 'provider', 'admin'].includes(user?.role) ? (
                <InviteParent setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to={isAuthenticated ? "/dashboard" : "/auth"} replace />
              )
            }
          />
          <Route
            path="/case-files/:caseId"
            element={
              isAuthenticated && ['attorney', 'court_rep', 'provider', 'admin'].includes(user?.role) ? (
                <CaseFileDetail setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to={isAuthenticated ? "/dashboard" : "/auth"} replace />
              )
            }
          />
          <Route
            path="/twilio-integration"
            element={
              isAuthenticated && user?.role === 'admin' ? (
                <TwilioIntegration setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to={isAuthenticated ? "/dashboard" : "/auth"} replace />
              )
            }
          />
          <Route
            path="/stripe-integration"
            element={
              isAuthenticated && user?.role === 'admin' ? (
                <StripeIntegration setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to={isAuthenticated ? "/dashboard" : "/auth"} replace />
              )
            }
          />
            {/* Admin Routes */}
                      <Route
                        path="/admin"
                        element={user?.role === 'admin' ? <AdminLayout /> : <Navigate to="/auth" replace />}
                                  >
                                    <Route index element={<AdminDashboard />} />
                                    <Route path="users" element={<AdminUsers />} />
                                    <Route path="users/:userId" element={<AdminUserDetail />} />
                                    <Route path="communications" element={<AdminCommunications />} />
                                    <Route path="security" element={<AdminSecurity />} />
                                    <Route path="cases" element={<AdminCases />} />
                                    <Route path="finance" element={<AdminFinance />} />
                                    <Route path="integrations" element={<AdminIntegrations />} />
                                    <Route path="settings" element={<AdminSettings />} />
                                    <Route path="audit" element={<AdminAudit />} />
                                    <Route path="admins" element={<AdminAdmins />} />
                          </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors duration={4000} closeButton />
    </div>
  );
}

// Main App component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
